import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, ListSubheader, FormControlLabel, Switch,
  Box, Alert, CircularProgress, Typography, Chip
} from '@mui/material';
import { apiService } from '../services/apiService';

const providers = [
  { value: 'dropbox', label: 'Dropbox' },
  { value: 'gdrive', label: 'Google Drive' },
  { value: 'gcs', label: 'Google Cloud Storage' },
  { value: 'aws-s3', label: 'AWS S3' },
  { value: 'sftp', label: 'SFTP / SCP (your own server)' }
];


const credentialHelp = {
  dropbox: 'Enter your Dropbox App Key and Secret, then click "Authorize" to sign in. Create an app at https://www.dropbox.com/developers/apps',
  gdrive: 'Enter your Google Cloud OAuth2 Client ID and Secret, then click "Authorize" to sign in.',
  gcs: 'Paste your GCS service account JSON key and bucket name',
  'aws-s3': 'Enter your AWS access key ID, secret access key, region, and bucket name.',
  sftp: "Enter your server's host, port, and username. No password is stored — add Asterism's public key (below) to that user's ~/.ssh/authorized_keys so Asterism can connect."
};

// `isOrganization` marks an organization-scoped config, which alone may be pre-selected on the
// observation forms (#1454).
export default function ExternalStorageDialog({ open, onClose, onSave, config, loading, error, orgProviders = [], isOrganization = false }) {
  const isEdit = !!config;

  const [formData, setFormData] = useState({
    display_name: '',
    provider: 'dropbox',
    destination_path: '/NINA-Images',
    custom_path: '',
    comment: '',
    transfer_content: 'raw',
    delete_from_minio: false,
    compress_before_transfer: true,
    default_selected: false,
    enabled: true,
    // Credentials fields
    app_key: '',
    app_secret: '',
    bucket_name: '',
    service_account_json: '',
    client_id: '',
    client_secret: '',
    // AWS S3
    region: '',
    access_key_id: '',
    secret_access_key: '',
    // SFTP / SCP (non-secret connection details)
    host: '',
    port: 22,
    username: ''
  });

  // Organization Storage Provider (#1021): when the provider is an org-backed
  // config, its dropdown value is `org:<orgConfigId>`. Such a config DERIVES the
  // provider, credentials, connection, and root path from the org config — those
  // inputs are hidden — and only the delivery fields are editable here.
  const derivedFromId = formData.provider && String(formData.provider).startsWith('org:')
    ? String(formData.provider).slice(4)
    : null;
  const isDerived = !!derivedFromId;
  const selectedOrgProvider = isDerived
    ? orgProviders.find(p => String(p._id) === derivedFromId)
    : null;

  // Asterism's SSH public key for the SFTP provider (fetched on demand).
  const [sshPublicKey, setSshPublicKey] = useState('');
  const [sshKeyCopied, setSshKeyCopied] = useState(false);

  // Google Drive OAuth state
  const [gdriveAuth, setGdriveAuth] = useState({
    authorized: false,
    email: '',
    ticket_id: null,
    redirect_uri: null,
    loading: false,
    error: null
  });

  // Dropbox OAuth state
  const [dropboxAuth, setDropboxAuth] = useState({
    authorized: false,
    email: '',
    ticket_id: null,
    redirect_uri: null,
    loading: false,
    error: null
  });

  const popupRef = useRef(null);

  // Authoritative redirect URIs the server will use (honors
  // OAUTH_REDIRECT_BASE_URL). Fetched when the dialog opens so the hint always
  // matches the real OAuth flow instead of guessing from the browser origin.
  const [serverRedirectUris, setServerRedirectUris] = useState({ gdrive: null, dropbox: null });
  // 'loading' | 'ok' | 'error' — so the hint can show a clear status instead of
  // silently flashing the browser origin (a LAN IP) before/if the fetch fails.
  const [redirectUrisStatus, setRedirectUrisStatus] = useState('loading');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setRedirectUrisStatus('loading');
    (async () => {
      try {
        const res = await apiService.getOAuthRedirectUris();
        if (cancelled) return;
        if (res?.data?.gdrive || res?.data?.dropbox) {
          setServerRedirectUris({ gdrive: res.data.gdrive || null, dropbox: res.data.dropbox || null });
          setRedirectUrisStatus('ok');
        } else {
          setRedirectUrisStatus('error');
        }
      } catch (e) {
        if (!cancelled) setRedirectUrisStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Resolve the redirect URI to display for a provider: prefer the value from
  // the just-started auth flow, then the server's authoritative value; while
  // that's loading show a placeholder; only fall back to the browser origin if
  // the server couldn't be reached (older API without the endpoint).
  const displayRedirectUri = (provider, authRedirectUri) => {
    if (authRedirectUri) return authRedirectUri;
    if (serverRedirectUris[provider]) return serverRedirectUris[provider];
    if (redirectUrisStatus === 'loading') return 'Determining redirect URI…';
    return `${window.location.origin}/api/v1/external-storage/${provider}/callback`;
  };

  const resetOAuthState = () => {
    setGdriveAuth({ authorized: false, email: '', ticket_id: null, redirect_uri: null, loading: false, error: null });
    setDropboxAuth({ authorized: false, email: '', ticket_id: null, redirect_uri: null, loading: false, error: null });
  };

  // Reset form when dialog opens/closes or config changes
  useEffect(() => {
    if (config) {
      setFormData({
        display_name: config.display_name || '',
        // A derived config's provider dropdown reflects its org source.
        provider: config.derived_from_org_config_id
          ? `org:${config.derived_from_org_config_id}`
          : (config.provider || 'dropbox'),
        destination_path: config.destination_path || '/NINA-Images',
        custom_path: config.custom_path || (
          config.file_organization === 'by_project_target' ? '$PROJECT/$TARGET' :
          config.file_organization === 'by_target' ? '$TARGET' : ''
        ),
        comment: config.comment || '',
        transfer_content: config.transfer_content || 'raw',
        delete_from_minio: config.delete_from_minio || false,
        compress_before_transfer: config.compress_before_transfer !== false,
        default_selected: config.default_selected === true,
        enabled: config.enabled !== false,
        app_key: '',
        app_secret: '',
        bucket_name: '',
        service_account_json: '',
        client_id: '',
        client_secret: '',
        region: '',
        access_key_id: '',
        secret_access_key: '',
        host: config.host || '',
        port: config.port || 22,
        username: config.username || ''
      });
      // For edit mode with gdrive, show as "already authorized"
      if (config.provider === 'gdrive') {
        setGdriveAuth({ authorized: true, email: config.gdrive_email || '(existing credentials)', ticket_id: null, redirect_uri: null, loading: false, error: null });
      } else {
        setGdriveAuth({ authorized: false, email: '', ticket_id: null, redirect_uri: null, loading: false, error: null });
      }
      // For edit mode with dropbox, show as "already authorized"
      if (config.provider === 'dropbox') {
        setDropboxAuth({ authorized: true, email: config.dropbox_email || '(existing credentials)', ticket_id: null, redirect_uri: null, loading: false, error: null });
      } else {
        setDropboxAuth({ authorized: false, email: '', ticket_id: null, redirect_uri: null, loading: false, error: null });
      }
    } else {
      setFormData({
        display_name: '',
        provider: 'dropbox',
        destination_path: '/NINA-Images',
        custom_path: '',
        comment: '',
        transfer_content: 'raw',
        delete_from_minio: false,
        compress_before_transfer: true,
        default_selected: false,
        enabled: true,
        app_key: '',
        app_secret: '',
        bucket_name: '',
        service_account_json: '',
        client_id: '',
        client_secret: '',
        region: '',
        access_key_id: '',
        secret_access_key: '',
        host: '',
        port: 22,
        username: ''
      });
      resetOAuthState();
    }
  }, [config, open]);

  // Fetch Asterism's SSH public key once the SFTP provider is selected so the
  // user can copy it into their server's authorized_keys.
  useEffect(() => {
    if (open && formData.provider === 'sftp' && !sshPublicKey) {
      apiService.getSshPublicKey()
        .then(res => { if (res?.success && res.data?.public_key) setSshPublicKey(res.data.public_key); })
        .catch(() => { /* surfaced via the empty-state hint below */ });
    }
  }, [open, formData.provider, sshPublicKey]);

  const handleGdriveSignIn = async () => {
    if (!formData.client_id || !formData.client_secret) {
      setGdriveAuth(prev => ({ ...prev, error: 'Enter your Client ID and Client Secret first.' }));
      return;
    }

    setGdriveAuth(prev => ({ ...prev, loading: true, error: null }));
    try {
      const result = await apiService.getGdriveAuthUrl(formData.client_id, formData.client_secret);
      if (!result.success) {
        setGdriveAuth(prev => ({ ...prev, loading: false, error: result.error?.message || 'Failed to get auth URL' }));
        return;
      }

      const { auth_url, ticket_id, redirect_uri } = result.data;
      setGdriveAuth(prev => ({ ...prev, redirect_uri }));
      const width = 500, height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      popupRef.current = window.open(
        auth_url,
        'gdrive-auth',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
      );

      // Poll the SERVER for ticket status. Cross-origin redirects break
      // postMessage and window.close() in the popup, so we ask the server
      // directly whether the OAuth callback has completed.
      const pollTicket = setInterval(async () => {
        try {
          const resp = await apiService.getGdriveTicketStatus(ticket_id);
          const status = resp?.data?.status;
          if (status === 'complete') {
            clearInterval(pollTicket);
            setGdriveAuth({
              authorized: true,
              email: resp.data.email || '',
              ticket_id: ticket_id,
              loading: false,
              error: null
            });
            return;
          }
          if (status === 'expired') {
            clearInterval(pollTicket);
            setGdriveAuth(prev => ({
              ...prev,
              loading: false,
              error: 'Authorization session expired. Please try again.'
            }));
            return;
          }
        } catch (e) { /* keep polling */ }
      }, 2000);
    } catch (err) {
      setGdriveAuth(prev => ({ ...prev, loading: false, error: 'Failed to start authorization' }));
    }
  };

  const handleDropboxSignIn = async () => {
    if (!formData.app_key || !formData.app_secret) {
      setDropboxAuth(prev => ({ ...prev, error: 'Enter your App Key and App Secret first.' }));
      return;
    }

    setDropboxAuth(prev => ({ ...prev, loading: true, error: null }));
    try {
      const result = await apiService.getDropboxAuthUrl(formData.app_key, formData.app_secret);
      if (!result.success) {
        setDropboxAuth(prev => ({ ...prev, loading: false, error: result.error?.message || 'Failed to get auth URL' }));
        return;
      }

      const { auth_url, ticket_id, redirect_uri } = result.data;
      setDropboxAuth(prev => ({ ...prev, redirect_uri }));
      const width = 500, height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      popupRef.current = window.open(
        auth_url,
        'dropbox-auth',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
      );

      // Poll the SERVER for ticket status. Dropbox's cross-origin redirect
      // breaks postMessage, localStorage, and window.close() in the popup,
      // so we bypass all client-side communication and ask the server
      // directly whether the OAuth callback has completed.
      const pollTicket = setInterval(async () => {
        try {
          const resp = await apiService.getDropboxTicketStatus(ticket_id);
          const status = resp?.data?.status;
          if (status === 'complete') {
            clearInterval(pollTicket);
            setDropboxAuth({
              authorized: true,
              email: resp.data.email || '',
              ticket_id: ticket_id,
              loading: false,
              error: null
            });
            return;
          }
          if (status === 'expired') {
            clearInterval(pollTicket);
            setDropboxAuth(prev => ({
              ...prev,
              loading: false,
              error: 'Authorization session expired. Please try again.'
            }));
            return;
          }
        } catch (e) { /* keep polling */ }
      }, 2000);
    } catch (err) {
      setDropboxAuth(prev => ({ ...prev, loading: false, error: 'Failed to start authorization' }));
    }
  };

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    // Reset OAuth auth when provider changes
    if (field === 'provider') {
      resetOAuthState();
    }
    // Reset gdrive auth if user changes client credentials after authorizing
    if ((field === 'client_id' || field === 'client_secret') && gdriveAuth.ticket_id) {
      setGdriveAuth({ authorized: false, email: '', ticket_id: null, redirect_uri: null, loading: false, error: null });
    }
    // Reset dropbox auth if user changes app credentials after authorizing
    if ((field === 'app_key' || field === 'app_secret') && dropboxAuth.ticket_id) {
      setDropboxAuth({ authorized: false, email: '', ticket_id: null, redirect_uri: null, loading: false, error: null });
    }
  };

  const handleSwitchChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.checked }));
  };

  const handleSubmit = () => {
    // Organization Storage Provider (#1021): send the reference + delivery
    // overrides only. Provider / credentials / root path come from the org
    // config server-side, so we never send them here.
    if (isDerived) {
      onSave({
        display_name: formData.display_name,
        derived_from_org_config_id: derivedFromId,
        custom_path: formData.custom_path,
        comment: formData.comment,
        transfer_content: formData.transfer_content,
        delete_from_minio: formData.delete_from_minio,
        compress_before_transfer: formData.compress_before_transfer,
        enabled: formData.enabled,
      }, config?._id);
      return;
    }

    const payload = {
      display_name: formData.display_name,
      provider: formData.provider,
      destination_path: formData.destination_path,
      custom_path: formData.custom_path,
      comment: formData.comment,
      transfer_content: formData.transfer_content,
      delete_from_minio: formData.delete_from_minio,
      compress_before_transfer: formData.compress_before_transfer,
      enabled: formData.enabled
    };
    if (isOrganization) payload.default_selected = formData.default_selected;

    if (formData.provider === 'gdrive') {
      // Use OAuth ticket if we have one from a fresh authorization
      if (gdriveAuth.ticket_id) {
        payload.gdrive_auth_ticket = gdriveAuth.ticket_id;
      } else if (!isEdit) {
        return; // New config requires authorization
      }
      // For edit without re-auth, we send no credentials (server keeps existing)
    } else if (formData.provider === 'dropbox') {
      // Use OAuth ticket if we have one from a fresh authorization
      if (dropboxAuth.ticket_id) {
        payload.dropbox_auth_ticket = dropboxAuth.ticket_id;
      } else if (!isEdit) {
        return; // New config requires authorization
      }
      // For edit without re-auth, we send no credentials (server keeps existing)
    } else if (formData.provider === 'gcs') {
      // Build credentials object for GCS (direct credentials, no OAuth)
      const hasNewCredentials = !!(formData.bucket_name && formData.service_account_json);

      if (hasNewCredentials) {
        payload.credentials = {
          bucket_name: formData.bucket_name,
          service_account_json: formData.service_account_json
        };
      } else if (!isEdit) {
        return; // New config requires credentials
      }
    } else if (formData.provider === 'aws-s3') {
      // Build credentials object for AWS S3 (direct credentials, no OAuth)
      const hasNewCredentials = !!(
        formData.bucket_name && formData.region &&
        formData.access_key_id && formData.secret_access_key
      );

      if (hasNewCredentials) {
        payload.credentials = {
          bucket_name: formData.bucket_name,
          region: formData.region,
          access_key_id: formData.access_key_id,
          secret_access_key: formData.secret_access_key
        };
      } else if (!isEdit) {
        return; // New config requires credentials
      }
    } else if (formData.provider === 'sftp') {
      // SFTP has no stored secret — it authenticates with Asterism's own SSH
      // key. Send the connection details only.
      payload.host = formData.host;
      payload.port = Number(formData.port) || 22;
      payload.username = formData.username;
    }

    onSave(payload, config?._id);
  };

  // A derived config only needs a display name — everything else is inherited.
  const canSubmit = isDerived ? !!formData.display_name : (
    formData.display_name && formData.destination_path && (
    isEdit || (
      formData.provider === 'dropbox' ? dropboxAuth.authorized :
      formData.provider === 'gdrive' ? gdriveAuth.authorized :
      formData.provider === 'sftp' ? (formData.host && formData.username) :
      formData.provider === 'aws-s3'
        ? (formData.bucket_name && formData.region && formData.access_key_id && formData.secret_access_key)
        : (formData.bucket_name && formData.service_account_json)
    )
  ));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Storage Configuration' : 'Add External Storage'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            select
            label="Provider"
            value={formData.provider}
            onChange={handleChange('provider')}
            required
            fullWidth
            disabled={isEdit}
            helperText={orgProviders.length > 0
              ? 'Pick a provider, or reuse an Organization storage destination (credentials + root path come from the org).'
              : undefined}
          >
            {providers.map(p => (
              <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
            ))}
            {orgProviders.length > 0 && (
              <ListSubheader>Organization Storage</ListSubheader>
            )}
            {orgProviders.map(p => (
              <MenuItem key={`org:${p._id}`} value={`org:${p._id}`}>
                {p.display_name}{p.organization_name ? ` — ${p.organization_name}` : ''}
              </MenuItem>
            ))}
          </TextField>

          {isDerived && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              Using organization storage
              {selectedOrgProvider?.organization_name ? ` from ${selectedOrgProvider.organization_name}` : ''}.
              Credentials and the root path are managed at the organization level; set a Display Name and
              customize the delivery options below.
            </Alert>
          )}

          <TextField
            label="Display Name"
            value={formData.display_name}
            onChange={handleChange('display_name')}
            required
            fullWidth
            placeholder="e.g., My Dropbox"
          />

          {!isDerived && credentialHelp[formData.provider] && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              {credentialHelp[formData.provider]}
            </Alert>
          )}

          {formData.provider === 'dropbox' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <TextField
                label="App Key"
                value={formData.app_key}
                onChange={handleChange('app_key')}
                required={!isEdit}
                fullWidth
                placeholder={isEdit ? '(leave blank to keep existing)' : 'Your Dropbox app key'}
                helperText={isEdit ? 'Leave blank to keep existing credentials' : 'From Dropbox App Console > Settings'}
              />
              <TextField
                label="App Secret"
                value={formData.app_secret}
                onChange={handleChange('app_secret')}
                required={!isEdit}
                fullWidth
                type="password"
                placeholder={isEdit ? '(leave blank to keep existing)' : 'Your Dropbox app secret'}
                helperText={isEdit ? 'Leave blank to keep existing credentials' : ''}
              />

              <Alert severity="warning" sx={{ py: 0.5 }}>
                <Typography variant="body2">
                  In your Dropbox App Console, add this <strong>Redirect URI</strong>:
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.5, wordBreak: 'break-all' }}>
                  {displayRedirectUri('dropbox', dropboxAuth.redirect_uri)}
                </Typography>
              </Alert>

              {dropboxAuth.error && (
                <Alert severity="error" sx={{ py: 0.5 }}>{dropboxAuth.error}</Alert>
              )}

              {dropboxAuth.authorized ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, bgcolor: 'success.light', borderRadius: 1, opacity: 0.9 }}>
                  <Chip label="Authorized" color="success" size="small" />
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {dropboxAuth.email || '(existing credentials)'}
                  </Typography>
                  <Button size="small" variant="contained" color="primary" onClick={handleDropboxSignIn} disabled={dropboxAuth.loading}>
                    Reauthorize
                  </Button>
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  onClick={handleDropboxSignIn}
                  disabled={dropboxAuth.loading || (!isEdit && (!formData.app_key || !formData.app_secret))}
                  startIcon={dropboxAuth.loading ? <CircularProgress size={16} /> : null}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  {dropboxAuth.loading ? 'Waiting for authorization...' : 'Authorize with Dropbox'}
                </Button>
              )}
            </Box>
          )}

          {formData.provider === 'gdrive' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <TextField
                label="Client ID"
                value={formData.client_id}
                onChange={handleChange('client_id')}
                required={!isEdit}
                fullWidth
                placeholder={isEdit ? '(leave blank to keep existing)' : 'your-client-id.apps.googleusercontent.com'}
                helperText={isEdit ? 'Leave blank to keep existing credentials' : 'From Google Cloud Console > APIs & Services > Credentials'}
              />
              <TextField
                label="Client Secret"
                value={formData.client_secret}
                onChange={handleChange('client_secret')}
                required={!isEdit}
                fullWidth
                type="password"
                placeholder={isEdit ? '(leave blank to keep existing)' : 'Your OAuth2 client secret'}
                helperText={isEdit ? 'Leave blank to keep existing credentials' : ''}
              />

              <Alert severity="warning" sx={{ py: 0.5 }}>
                <Typography variant="body2">
                  In your Google Cloud Console OAuth client, add this <strong>Authorized redirect URI</strong>:
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.5, wordBreak: 'break-all' }}>
                  {displayRedirectUri('gdrive', gdriveAuth.redirect_uri)}
                </Typography>
              </Alert>

              {gdriveAuth.error && (
                <Alert severity="error" sx={{ py: 0.5 }}>{gdriveAuth.error}</Alert>
              )}

              {gdriveAuth.authorized ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, bgcolor: 'success.light', borderRadius: 1, opacity: 0.9 }}>
                  <Chip label="Authorized" color="success" size="small" />
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {gdriveAuth.email || '(existing credentials)'}
                  </Typography>
                  <Button size="small" variant="contained" color="primary" onClick={handleGdriveSignIn} disabled={gdriveAuth.loading}>
                    Reauthorize
                  </Button>
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  onClick={handleGdriveSignIn}
                  disabled={gdriveAuth.loading || (!isEdit && (!formData.client_id || !formData.client_secret))}
                  startIcon={gdriveAuth.loading ? <CircularProgress size={16} /> : null}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  {gdriveAuth.loading ? 'Waiting for authorization...' : 'Authorize with Google'}
                </Button>
              )}
            </Box>
          )}

          {formData.provider === 'gcs' && (
            <>
              <TextField
                label="Bucket Name"
                value={formData.bucket_name}
                onChange={handleChange('bucket_name')}
                required={!isEdit}
                fullWidth
                placeholder="my-astronomy-bucket"
              />
              <TextField
                label="Service Account JSON"
                value={formData.service_account_json}
                onChange={handleChange('service_account_json')}
                required={!isEdit}
                fullWidth
                multiline
                rows={4}
                type="password"
                placeholder={isEdit ? '(leave blank to keep existing)' : 'Paste your service account JSON key'}
                helperText={isEdit ? 'Leave blank to keep existing credentials' : ''}
              />
            </>
          )}

          {formData.provider === 'aws-s3' && (
            <>
              <TextField
                label="Bucket Name"
                value={formData.bucket_name}
                onChange={handleChange('bucket_name')}
                required={!isEdit}
                fullWidth
                placeholder="my-astronomy-bucket"
              />
              <TextField
                label="Region"
                value={formData.region}
                onChange={handleChange('region')}
                required={!isEdit}
                fullWidth
                placeholder="us-east-1"
              />
              <TextField
                label="Access Key ID"
                value={formData.access_key_id}
                onChange={handleChange('access_key_id')}
                required={!isEdit}
                fullWidth
                placeholder={isEdit ? '(leave blank to keep existing)' : 'AKIA…'}
                helperText={isEdit ? 'Leave blank to keep existing credentials' : ''}
              />
              <TextField
                label="Secret Access Key"
                value={formData.secret_access_key}
                onChange={handleChange('secret_access_key')}
                required={!isEdit}
                fullWidth
                type="password"
                placeholder={isEdit ? '(leave blank to keep existing)' : 'Your AWS secret access key'}
                helperText={isEdit ? 'Leave blank to keep existing credentials' : ''}
              />
            </>
          )}

          {formData.provider === 'sftp' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <TextField
                  label="Host"
                  value={formData.host}
                  onChange={handleChange('host')}
                  required
                  sx={{ flex: 3 }}
                  placeholder="data.myobservatory.org"
                />
                <TextField
                  label="Port"
                  value={formData.port}
                  onChange={handleChange('port')}
                  type="number"
                  sx={{ flex: 1 }}
                  placeholder="22"
                />
              </Box>
              <TextField
                label="Username"
                value={formData.username}
                onChange={handleChange('username')}
                required
                fullWidth
                placeholder="e.g., astro"
              />

              <Alert severity="info" sx={{ py: 0.5 }}>
                <Typography variant="body2">
                  Add this <strong>public key</strong> to <code>~/.ssh/authorized_keys</code> for
                  the user above on your server, so Asterism can connect:
                </Typography>
                {sshPublicKey ? (
                  <>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: 'monospace', fontSize: '0.72rem', mt: 0.75, p: 1,
                            bgcolor: 'action.hover', borderRadius: 1, wordBreak: 'break-all' }}
                    >
                      {sshPublicKey}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ mt: 0.75 }}
                      onClick={() => {
                        navigator.clipboard?.writeText(sshPublicKey);
                        setSshKeyCopied(true);
                        setTimeout(() => setSshKeyCopied(false), 2000);
                      }}
                    >
                      {sshKeyCopied ? 'Copied!' : 'Copy public key'}
                    </Button>
                  </>
                ) : (
                  <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                    Loading Asterism's public key…
                  </Typography>
                )}
              </Alert>
              <Typography variant="caption" color="text.secondary">
                On the first successful connection test, Asterism records the server's host-key
                fingerprint and rejects later connections if it changes.
              </Typography>
            </Box>
          )}

          {isDerived ? (
            <TextField
              label="Root Path (from organization)"
              value={selectedOrgProvider?.destination_path || '(defined at the organization level)'}
              fullWidth
              disabled
              helperText="The root path is inherited from the organization storage config and can't be changed here."
            />
          ) : (
            <TextField
              label="Root Path"
              value={formData.destination_path}
              onChange={handleChange('destination_path')}
              required
              fullWidth
              placeholder="/NINA-Images"
              helperText="Base folder path where files will be uploaded"
            />
          )}

          <TextField
            label="Storage Path Template"
            value={formData.custom_path}
            onChange={(e) => {
              // Allow valid Windows path characters, slashes, $USER, $PROJECT, $TARGET
              const value = e.target.value.replace(/[<>"|?*:]/g, '');
              setFormData(prev => ({ ...prev, custom_path: value }));
            }}
            fullWidth
            placeholder="e.g. $USER/$PROJECT/$TARGET (leave empty for flat)"
            helperText="Optional. Use $USER, $PROJECT, $TARGET with slashes/text to build subfolders. Leave empty to place files directly in the root path (flat)."
          />

          <TextField
            label="Comment (optional)"
            value={formData.comment}
            onChange={handleChange('comment')}
            fullWidth
            multiline
            rows={2}
            placeholder="Notes about this storage configuration"
          />

          <TextField
            select
            label="File Content"
            value={formData.transfer_content}
            onChange={handleChange('transfer_content')}
            fullWidth
            helperText="Choose which versions of files to transfer to this destination"
          >
            <MenuItem value="raw">Raw only</MenuItem>
            <MenuItem value="calibrated">Calibrated only</MenuItem>
            <MenuItem value="both">Both raw and calibrated</MenuItem>
          </TextField>

          <FormControlLabel
            control={
              <Switch
                checked={formData.delete_from_minio}
                onChange={handleSwitchChange('delete_from_minio')}
              />
            }
            label="Automatic delete"
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.compress_before_transfer}
                onChange={handleSwitchChange('compress_before_transfer')}
              />
            }
            label="Compress before transfer (Rice .fz)"
          />

          {isOrganization && (
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.default_selected}
                    onChange={handleSwitchChange('default_selected')}
                  />
                }
                label="Selected by default for new observations"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6, mt: -0.5 }}>
                Pre-checks this destination when an organization member creates an observation. Members can still uncheck it.
              </Typography>
            </Box>
          )}

          {isEdit && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.enabled}
                  onChange={handleSwitchChange('enabled')}
                />
              }
              label="Enabled"
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit || loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {isEdit ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
